import axios from 'axios';
import moment from 'moment';
import { constants } from '../../Helpers/constantsFile';
import { readDate, timeLabel } from '../../utils/appDate';

/**
 * A waiter's own recent orders, for the strip on the order pad.
 *
 * ── Why this needs no filtering here ────────────────────────────────────────
 *
 * `getOrdersByBusinessId` narrows the query itself:
 *
 *     ...(isWaiter && { user: user.id })
 *
 * So a waiter's token comes back holding only that waiter's orders, and this
 * module never has to know whose they are. Canceled orders are excluded by the
 * same controller.
 *
 * ── Why the fetch exists at all ─────────────────────────────────────────────
 *
 * A waiter's session lasts exactly one order — `printedNotice` ends it when
 * the receipt is done — so there is no in-session history to draw a strip
 * from. The list has to come from the server each time they sign in.
 */

const auth = (token) => ({ headers: { Authorization: token } });

/** Today, as the plain calendar day the endpoint expects. */
export const todayKey = () => moment().format('YYYY-MM-DD');

/**
 * Today's orders, newest first.
 *
 * Only today: a waiter reprinting or amending is dealing with a table that is
 * still in the room. Yesterday's orders are the Orders screen's business, and
 * a waiter cannot reach that screen anyway.
 */
export async function fetchMyOrders({ businessId, token, signal }) {
  const day = todayKey();

  const { data } = await axios.get(
    `${constants.baseUrl}/orders/get-business-orders/${businessId}` +
      `?startDate=${day}&endDate=${day}`,
    { ...auth(token), cancelToken: signal }
  );

  const orders = data?.data?.orders || [];

  return [...orders].sort(
    (a, b) => readDate(b?.date).valueOf() - readDate(a?.date).valueOf()
  );
}

/* ── reading an order ───────────────────────────────────────────────────── */

/**
 * Statuses that mean the money has already been dealt with at the counter.
 *
 * Editing one of these would move a total that has been collected, so those
 * cards are view-and-print only. The full enum on the model is pending, paid,
 * invoiced, preparing, ready, served, completed, canceled — the three here are
 * the settled ones, and canceled never reaches this list.
 */
const SETTLED = ['paid', 'invoiced', 'completed'];

export function isSettled(order) {
  return SETTLED.indexOf(String(order?.orderStatus || '').toLowerCase()) !== -1;
}

export function isInvoiceOrder(order) {
  return String(order?.paymentType || '').toLowerCase() === 'invoice';
}

export const orderItems = (order) => (Array.isArray(order?.items) ? order.items : []);

/** How many plates, not how many lines — two of one dish is two things. */
export function itemCount(order) {
  return orderItems(order).reduce((sum, item) => sum + (Number(item.quantity) || 0), 0);
}

export const orderTime = (order) => timeLabel(order?.date);

/**
 * The shape the print dialog wants.
 *
 * Built from the order rather than from the cart, so a reprint shows what was
 * actually sold — not whatever happens to be on the pad now. `date` is passed
 * through untouched; the receipt reads it with the wall-clock rule.
 */
export function receiptDataFromOrder(order) {
  return {
    orderNumber: order?.orderNumber,
    discount: Number(order?.discount) || 0,
    date: order?.date,
    // Carried off the saved order so a reprint shows the tax that was charged
    // on the day, not whatever the business rate is now.
    vatRate: Number(order?.vatRate) || 0,
    vatAmount: Number(order?.vatAmount) || 0,
    totalWithVat: order?.totalWithVat,
    waiterName: (order?.user && order.user.name) || '',
    products: orderItems(order).map((item) => ({
      name: item.name,
      qty: item.quantity,
      salePrice: item.price,
    })),
  };
}
